import React, { Fragment } from 'react';
import { PropTypes } from 'prop-types';
import { Box, Flex, Hide } from 'roo-ui/components';
import PulsingLoadingBlock from 'components/PulsingLoadingBlock';
import theme from 'lib/theme';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { PlainSkeletonCard, OffersWrapper, ShadowedSkeletonCard, BorderedSkeletonCard } from './primitives';

const RoomSkeletonCard = () => {
  const { isLessThanBreakpoint } = useBreakpoints();
  const isMobileWidth = isLessThanBreakpoint(0);

  return (
    <PlainSkeletonCard mb={[0, 0, 5]} width={['100%', 'initial']}>
      <Flex flexDirection={['column', 'row', 'column']}>
        <Hide xs sm>
          <PulsingLoadingBlock height={[32, 24]} width={['100%', 240]} mb={5} borderRadius="rounded" />
        </Hide>
        <Hide xs>
          <PulsingLoadingBlock
            height={theme.imageSizes.roomTypeThumbnail.height}
            width={theme.imageSizes.roomTypeThumbnail.width}
            borderRadius="default"
            mb={4}
            mr={4}
          />
        </Hide>
        <Flex mb={4} flexDirection="column">
          {new Array(isMobileWidth ? 3 : 1).fill(0).map((_, i) => (
            <Flex key={`offer-inclusion-${i}`} mb={i === 2 ? 6 : 4}>
              <PulsingLoadingBlock height={24} width={24} mb={0} mr={3} />
              <PulsingLoadingBlock height={24} width={[210, 100]} mb={0} />
            </Flex>
          ))}
        </Flex>
        <Hide sm md>
          <PulsingLoadingBlock height={24} width={[160, 200]} mb={[4, 0]} />
        </Hide>
        <Hide sm md lg>
          <PulsingLoadingBlock height={44} width="100%" borderRadius="default" mb={0} />
        </Hide>
      </Flex>
    </PlainSkeletonCard>
  );
};

const OfferSkeletonCard = () => (
  <BorderedSkeletonCard mb={4} p={4} border={[0, 1]} borderColor="greys.dusty">
    <Flex flexDirection="row" alignItems="center" flexWrap="wrap">
      <Box flexBasis="100%">
        <PulsingLoadingBlock height={32} width={334} mb={7} borderRadius="rounded" />
      </Box>
      <Flex flexBasis="50%" flexDirection="column">
        {new Array(3).fill(0).map((_, i) => (
          <Flex key={`offer-inclusion-${i}`} mb={i === 2 ? 0 : 4}>
            <PulsingLoadingBlock height={24} width={24} mb={0} mr={3} />
            <PulsingLoadingBlock height={24} width={210} mb={0} />
          </Flex>
        ))}
      </Flex>
      <Flex flexBasis="50%" flexDirection="column">
        <PulsingLoadingBlock height={24} width={156} mb={4} />
        <PulsingLoadingBlock height={44} width={196} borderRadius="default" />
      </Flex>
    </Flex>
  </BorderedSkeletonCard>
);

const CollapsedOfferSkeletonCard = () => (
  <BorderedSkeletonCard mb={0} p={4} border={[0, 0, 1]}>
    <Flex flexDirection="row" alignItems="center" flexWrap="wrap">
      <Box width="50%">
        <PulsingLoadingBlock height={[24, 32]} width={334} mb={[4, 7]} borderRadius="rounded" />
      </Box>
      <Hide xs>
        <PulsingLoadingBlock height={24} width={156} mb={7} />
      </Hide>
      <Flex flexBasis="100%" flexDirection="row" mb={[6, 0]}>
        {new Array(3).fill(0).map((_, i) => (
          <PulsingLoadingBlock key={`collapsed-inclusion-${i}`} height={24} width={24} mb={0} mr={3} />
        ))}
      </Flex>
      <Hide sm md lg>
        <PulsingLoadingBlock height={24} width={156} mb={0} />
      </Hide>
    </Flex>
  </BorderedSkeletonCard>
);

const LoaderSkeletonCard = React.memo(({ index }) => {
  return (
    <Fragment>
      <ShadowedSkeletonCard key={index} px={[6]} py={[5]} mb={[4, 4, 10]}>
        <RoomSkeletonCard />
        <Hide xs sm as={Box} flex="1 1 auto">
          <OffersWrapper mt={11}>
            <OfferSkeletonCard />
            <CollapsedOfferSkeletonCard />
          </OffersWrapper>
        </Hide>
      </ShadowedSkeletonCard>
      <Hide md lg>
        <ShadowedSkeletonCard mb={10}>
          <CollapsedOfferSkeletonCard />
        </ShadowedSkeletonCard>
      </Hide>
    </Fragment>
  );
});

LoaderSkeletonCard.displayName = 'LoaderSkeletonCard';

LoaderSkeletonCard.propTypes = {
  index: PropTypes.number,
};

LoaderSkeletonCard.defaultProps = {
  index: 0,
};

export default LoaderSkeletonCard;
